import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  TrendingUp, Users, Target, DollarSign, BarChart3, PieChart,
  ArrowUpRight, ArrowDownRight, Clock, Calendar, Trophy, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`;
  } else {
    return `₹${value.toLocaleString()}`;
  }
};

const AnalyticsDashboard = () => {
  const { user } = useOutletContext();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState([]);
  const [funnel, setFunnel] = useState({ leadFunnel: [], dealFunnel: [] });
  const [team, setTeam] = useState([]);
  const [followups, setFollowups] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    const token = localStorage.getItem('crm_token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [overviewRes, sourcesRes, funnelRes, teamRes, followupsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?period=${period}`, { headers }),
        axios.get(`${API}/analytics/lead-sources`, { headers }),
        axios.get(`${API}/analytics/conversion-funnel`, { headers }),
        axios.get(`${API}/analytics/team-performance`, { headers }),
        axios.get(`${API}/analytics/pending-followups`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setSources(sourcesRes.data.sources || []);
      setFunnel(funnelRes.data);
      setTeam(teamRes.data.team || []);
      setFollowups(followupsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-stone-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">Analytics Dashboard</h1>
          <p className="text-stone-500 mt-1">Track performance, conversions, and revenue</p>
        </div>
        <div className="mt-4 md:mt-0">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="quarter">Last 90 Days</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Leads</p>
                <p className="text-3xl font-bold mt-1">{overview?.leads?.total || 0}</p>
                <p className="text-blue-200 text-xs mt-1">
                  +{overview?.leads?.thisperiod || 0} this {period}
                </p>
              </div>
              <Users className="w-12 h-12 opacity-30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Conversion Rate</p>
                <p className="text-3xl font-bold mt-1">{overview?.leads?.conversionRate || 0}%</p>
                <p className="text-emerald-200 text-xs mt-1">
                  {overview?.leads?.converted || 0} converted
                </p>
              </div>
              <TrendingUp className="w-12 h-12 opacity-30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pipeline Value</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(overview?.revenue?.pipelineValue || 0)}</p>
                <p className="text-amber-200 text-xs mt-1">
                  {overview?.deals?.active || 0} active deals
                </p>
              </div>
              <Target className="w-12 h-12 opacity-30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Revenue Won</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(overview?.revenue?.wonValue || 0)}</p>
                <p className="text-green-200 text-xs mt-1">
                  {overview?.deals?.won || 0} deals won
                </p>
              </div>
              <DollarSign className="w-12 h-12 opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Follow-up Alerts */}
      {(followups.overdue > 0 || followups.today > 0) && (
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">Follow-up Reminders</p>
                <div className="flex gap-6 mt-1 text-sm">
                  {followups.overdue > 0 && (
                    <span className="text-red-600 font-medium">{followups.overdue} overdue</span>
                  )}
                  {followups.today > 0 && (
                    <span className="text-amber-700">{followups.today} due today</span>
                  )}
                  {followups.tomorrow > 0 && (
                    <span className="text-stone-600">{followups.tomorrow} tomorrow</span>
                  )}
                  {followups.thisWeek > 0 && (
                    <span className="text-stone-500">{followups.thisWeek} this week</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Lead Source ROI */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-600" />
              Lead Source Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sources.length === 0 ? (
              <p className="text-center text-stone-400 py-8">No data available</p>
            ) : (
              <div className="space-y-3">
                {sources.map((source, idx) => {
                  const maxLeads = Math.max(...sources.map(s => s.leads));
                  const barWidth = (source.leads / maxLeads) * 100;
                  
                  return (
                    <div key={source.source} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize text-stone-700">
                          {source.source.replace('_', ' ')}
                        </span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-stone-500">{source.leads} leads</span>
                          <span className={`font-medium ${source.conversionRate > 50 ? 'text-green-600' : source.conversionRate > 25 ? 'text-amber-600' : 'text-stone-500'}`}>
                            {source.conversionRate}%
                          </span>
                          <span className="font-bold text-emerald-700 w-24 text-right">
                            {formatCurrency(source.revenue)}
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Sales Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnel.dealFunnel.length === 0 ? (
              <p className="text-center text-stone-400 py-8">No data available</p>
            ) : (
              <div className="space-y-2">
                {funnel.dealFunnel.map((stage, idx) => {
                  const maxCount = Math.max(...funnel.dealFunnel.map(s => s.count));
                  const barWidth = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
                  
                  const stageColors = {
                    inquiry: 'from-blue-400 to-blue-500',
                    site_visit: 'from-purple-400 to-purple-500',
                    negotiation: 'from-amber-400 to-amber-500',
                    proposal: 'from-orange-400 to-orange-500',
                    documentation: 'from-teal-400 to-teal-500',
                    payment: 'from-emerald-400 to-emerald-500',
                    booking: 'from-green-400 to-green-500',
                    closed_won: 'from-green-500 to-green-600',
                  };
                  
                  return (
                    <div key={stage.stage} className="flex items-center gap-3">
                      <div className="w-28 text-xs text-stone-600 capitalize">
                        {stage.stage.replace('_', ' ')}
                      </div>
                      <div className="flex-1 h-6 bg-stone-100 rounded overflow-hidden relative">
                        <div 
                          className={`h-full bg-gradient-to-r ${stageColors[stage.stage] || 'from-stone-400 to-stone-500'} rounded transition-all flex items-center justify-end pr-2`}
                          style={{ width: `${Math.max(barWidth, 10)}%` }}
                        >
                          <span className="text-xs text-white font-medium">{stage.count}</span>
                        </div>
                      </div>
                      <div className="w-20 text-right text-xs font-medium text-emerald-700">
                        {formatCurrency(stage.value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Team Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {team.length === 0 ? (
            <p className="text-center text-stone-400 py-8">No team data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-stone-600">Team Member</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-stone-600">Leads</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-stone-600">Conv. Rate</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-stone-600">Deals Won</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-stone-600">Win Rate</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-stone-600">Revenue</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-stone-600">Pipeline</th>
                    <th className="text-center py-3 px-2 text-sm font-medium text-stone-600">Activities</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((member, idx) => (
                    <tr 
                      key={member.userId} 
                      className={`border-b border-stone-100 ${idx === 0 ? 'bg-amber-50' : ''}`}
                      data-testid={`team-member-${member.employeeId}`}
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900 text-sm flex items-center gap-2">
                              {member.name}
                              {idx === 0 && <Trophy className="w-4 h-4 text-amber-500" />}
                            </p>
                            <p className="text-xs text-stone-500">{member.employeeId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="text-sm">{member.metrics.leadsAssigned}</span>
                        <span className="text-xs text-stone-400 ml-1">({member.metrics.leadsConverted})</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className={`text-sm font-medium ${
                          member.metrics.conversionRate > 50 ? 'text-green-600' : 
                          member.metrics.conversionRate > 25 ? 'text-amber-600' : 'text-stone-500'
                        }`}>
                          {member.metrics.conversionRate}%
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="text-sm">{member.metrics.dealsWon}</span>
                        <span className="text-xs text-stone-400 ml-1">/ {member.metrics.dealsOwned}</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className={`text-sm font-medium ${
                          member.metrics.winRate > 50 ? 'text-green-600' : 
                          member.metrics.winRate > 25 ? 'text-amber-600' : 'text-stone-500'
                        }`}>
                          {member.metrics.winRate}%
                        </span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="font-bold text-emerald-700 text-sm">
                          {formatCurrency(member.metrics.revenue)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="text-sm text-stone-600">
                          {formatCurrency(member.metrics.pipeline)}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="text-sm bg-stone-100 px-2 py-1 rounded">
                          {member.metrics.activitiesThisMonth}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
